import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ProductCompareTheme } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { ISafeHTML, IWebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
} from "../../../models/product-compare";
import { trackCategoryPageView } from "../../../utils/analytics";
import { notEmpty } from "../../../utils/functional";
import { rootProductSelector } from "../../configurator/selectors";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { ProductCompareGrid as ProductCompareGridComponent } from "../components/ProductCompareGrid";
import { defaults } from "../defaults";
import { setRootProducts } from "../reducers";
import { getSelectedVariant } from "../selectors";

const trackCategoryView = (
    tiles: readonly ICompareTile[],
    priceSelectValue: string,
): void => {
    if (!tiles) {
        return;
    }
    // Obtain SKUs for current variants
    const skus = tiles
        .map((tile) => {
            const variant = getSelectedVariant(tile, priceSelectValue);
            return variant ? variant.skus[0] : null;
        })
        .filter(notEmpty);
    trackCategoryPageView(skus);
};

export const ProductCompareGrid = (props: {
    header: ISafeHTML;
    basketLink: IWebPageURL;
    financingLink: IWebPageURL;
    preselectedSize: string | null;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    tiles: readonly ICompareTile[];
    theme: ProductCompareTheme;
    showFirstAvailablePrice?: boolean;
    showRating?: boolean;
    showStartingAt?: boolean;
    isMinimal?: boolean;
}): React.ReactNode => {
    const priceSelectValue = useAppSelector(
        (state) => (state.productcompare || defaults).priceSelectValue,
    );
    const dispatch = useAppDispatch();
    const viewport = useAppSelector((state) => state.common.viewport);
    const rootProduct: IProduct | null = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );
    const rootProducts = useAppSelector(
        (state) => (state.productcompare || defaults).rootProducts,
    );

    const didMountRef = React.useRef(false);

    React.useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        trackCategoryView(props.tiles, priceSelectValue);
    }, [props.tiles, priceSelectValue]);

    const window = getWindow();
    const urlParams = new URLSearchParams(window ? window.location.search : "");
    const pageRootProduct = rootProduct ? rootProduct.id : null;

    return (
        <ProductCompareGridComponent
            header={props.header}
            tiles={props.tiles}
            basketLink={props.basketLink}
            financingLink={props.financingLink}
            sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
            preselectedSize={props.preselectedSize || null}
            priceSelectValue={priceSelectValue}
            pageRootProduct={pageRootProduct}
            theme={props.theme}
            urlParams={urlParams}
            viewport={viewport}
            showFirstAvailablePrice={props.showFirstAvailablePrice}
            showRating={props.showRating}
            showStartingAt={props.showStartingAt}
            setRootProducts={(rps) => {
                dispatch(
                    setRootProducts({
                        rootProducts: rps,
                    }),
                );
            }}
            rootProducts={rootProducts}
            isMinimal={props.isMinimal}
        />
    );
};
